import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Tag, Avatar, Badge } from 'antd';
import { 
  BellOutlined, 
  CloseOutlined, 
  CheckOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { Notification } from '../services/api';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import './NotificationPopup.css';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

const { Text, Title } = Typography;

interface NotificationPopupProps {
  notification: Notification;
  onClose: () => void;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationPopup: React.FC<NotificationPopupProps> = ({
  notification,
  onClose,
  onMarkAsRead,
  onDelete
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Animate in after component mounts
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300); // Match CSS transition duration
  };

  const handleMarkAsRead = () => {
    onMarkAsRead(notification.id);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(notification.id);
    handleClose();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'assignment':
      case 'batch_assignment':
        return <UserOutlined />;
      case 'duplicate_lead':
        return <ExclamationCircleOutlined />;
      case 'activity':
      case 'event_creation':
        return <CalendarOutlined />;
      case 'reminder':
        return <BellOutlined />;
      case 'info':
        return <InfoCircleOutlined />;
      case 'warning':
        return <ExclamationCircleOutlined />;
      case 'error':
        return <ExclamationCircleOutlined />;
      default:
        return <BellOutlined />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'assignment':
      case 'batch_assignment':
        return '#1890ff';
      case 'duplicate_lead':
        return '#fa8c16';
      case 'activity':
      case 'event_creation':
        return '#52c41a';
      case 'reminder':
        return '#722ed1';
      case 'info':
        return '#13c2c2';
      case 'warning':
        return '#faad14';
      case 'error':
        return '#ff4d4f';
      default:
        return '#8c8c8c';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'assignment':
        return 'Atribuição';
      case 'batch_assignment':
        return 'Atribuição em Lote';
      case 'duplicate_lead':
        return 'Lead Duplicado';
      case 'activity':
        return 'Evento';
      case 'event_creation':
        return 'Novo Evento';
      case 'reminder':
        return 'Lembrete';
      case 'info':
        return 'Informação';
      case 'warning':
        return 'Aviso';
      case 'error':
        return 'Erro';
      default:
        return type;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return '#ff4d4f';
      case 'high':
        return '#fa8c16';
      case 'medium':
        return '#1890ff';
      case 'low':
        return '#8c8c8c';
      default:
        return '#1890ff';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'Urgente';
      case 'high':
        return 'Alta';
      case 'medium':
        return 'Média';
      case 'low':
        return 'Baixa';
      default:
        return 'Média';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    // Get current time in Lisbon timezone
    const now = dayjs().tz('Europe/Lisbon');
    // Parse the notification time (assuming it's already in Lisbon time from backend)
    const notificationTime = dayjs(dateString).tz('Europe/Lisbon');
    const diffInMinutes = now.diff(notificationTime, 'minute');
    
    if (diffInMinutes < 1) return 'Agora mesmo';
    if (diffInMinutes < 60) return `Há ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Há ${Math.floor(diffInMinutes / 60)}h`;
    return `Há ${Math.floor(diffInMinutes / 1440)}d`;
  };

  return (
    <div 
      className={`notification-popup ${isVisible ? 'visible' : ''} ${isClosing ? 'closing' : ''}`}
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        maxWidth: '400px',
        minWidth: '320px',
        transform: isVisible ? 'translateX(0)' : 'translateX(100%)',
        opacity: isVisible ? 1 : 0,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Card
        size="small"
        style={{
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          border: 'none',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
        bodyStyle={{ padding: '16px' }}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Badge dot={!notification.read}>
              <Avatar 
                size={32}
                icon={getTypeIcon(notification.notification_type)}
                style={{ 
                  backgroundColor: getTypeColor(notification.notification_type),
                  color: 'white'
                }}
              />
            </Badge>
            <div>
              <Tag 
                color={getTypeColor(notification.notification_type)}
                style={{ margin: 0, fontSize: '11px', fontWeight: 600 }}
              >
                {getTypeLabel(notification.notification_type)}
              </Tag>
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>
                {formatTimeAgo(notification.created_at)}
              </div>
            </div>
          </div>
          <Button
            type="text"
            icon={<CloseOutlined />}
            size="small"
            onClick={handleClose}
            style={{
              color: '#8c8c8c',
              border: 'none',
              background: 'none',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: '12px' }}>
          <Title level={5} style={{ 
            margin: '0 0 8px 0', 
            fontSize: '14px',
            fontWeight: 600,
            color: '#262626',
            lineHeight: '1.4'
          }}>
            {notification.title}
          </Title>
          <Text style={{ 
            fontSize: '13px', 
            color: '#595959',
            lineHeight: '1.5',
            display: 'block'
          }}>
            {notification.message}
          </Text>
        </div>

        {/* Priority indicator */}
        {notification.priority && notification.priority !== 'medium' && (
          <div style={{ marginBottom: '12px' }}>
            <Tag 
              color={getPriorityColor(notification.priority)}
              style={{ fontSize: '10px', fontWeight: 600 }}
            >
              Prioridade {getPriorityLabel(notification.priority)}
            </Tag>
          </div>
        )}

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '8px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Space size="small">
            {!notification.read && (
              <Button
                type="text"
                size="small"
                icon={<CheckOutlined />}
                onClick={handleMarkAsRead}
                style={{
                  color: '#52c41a',
                  fontSize: '12px',
                  height: '24px',
                  padding: '0 8px'
                }}
              >
                Marcar como lida
              </Button>
            )}
          </Space>
          <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
            {dayjs(notification.created_at).tz('Europe/Lisbon').format('HH:mm')}
          </Text>
        </div>

        {/* Decorative elements */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${getTypeColor(notification.notification_type)} 0%, ${getTypeColor(notification.notification_type)}80 100%)`,
            borderRadius: '12px 12px 0 0'
          }}
        />
        
        {/* Urgent notification indicator */}
        {notification.priority === 'urgent' && (
          <div 
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#ff4d4f',
              animation: 'urgentPulse 2s infinite',
              boxShadow: '0 0 0 0 rgba(255, 77, 79, 0.7)'
            }}
          />
        )}
      </Card>
    </div>
  );
};

export default NotificationPopup;
